import { forwardRef, useImperativeHandle, useLayoutEffect, useRef, useState } from 'react';

/** Google Slides preview sometimes skips `load` after media (e.g. video); still swap panes so parents stay in sync. */
const SLIDE_LOAD_FALLBACK_MS = 4000;

/**
 * Two stacked Google Slides preview iframes: the visible pane stays on the current slide
 * while the hidden pane loads the next URL, then we swap on `load`. Avoids black flashes
 * and the blur/pixel shift from showing Slides API thumbnails over a different embed layout.
 *
 * `onVisibleSlideReady` fires after the visible pane shows the current `src` (including
 * first paint), so parent UIs can sync notes/thumbnails with what the audience actually sees.
 *
 * Ref API: `nudgeInSlideAdvance()` — dispatches a center tap on the visible iframe so
 * click-to-reveal builds advance (best-effort; may not work cross-origin in all browsers).
 *
 * Embedded Slides media (e.g. video) is controlled entirely inside Google’s iframe; the parent
 * page cannot start/stop playback or sync timestamps across windows — only slide URL + nudge.
 *
 * The hidden “standby” iframe is always muted so preloading the next URL cannot double-play audio.
 */
function DualGoogleSlidesEmbed(
  { src, title, allowFullScreen = false, onVisibleSlideReady, embedMuted = false },
  ref
) {
  const aRef = useRef(null);
  const bRef = useRef(null);
  const prevSrcRef = useRef(null);
  const activePaneRef = useRef(0);
  /** Which pane (0 = A, 1 = B) is visible; drives `muted` so the hidden loader iframe never plays audio. */
  const [visiblePane, setVisiblePane] = useState(0);
  const loadGenRef = useRef(0);
  const onReadyRef = useRef(onVisibleSlideReady);
  onReadyRef.current = onVisibleSlideReady;

  const blurBothPanes = () => {
    try {
      aRef.current?.blur();
    } catch {
      /* ignore */
    }
    try {
      bRef.current?.blur();
    } catch {
      /* ignore */
    }
  };

  useImperativeHandle(
    ref,
    () => ({
      blurEmbeds() {
        blurBothPanes();
      },
      nudgeInSlideAdvance() {
        const tryNudge = (el) => {
          if (!el) return false;
          if (getComputedStyle(el).visibility === 'hidden') return false;
          const rect = el.getBoundingClientRect();
          if (rect.width < 4 || rect.height < 4) return false;
          const x = Math.round(rect.left + rect.width / 2);
          const y = Math.round(rect.top + rect.height / 2);
          // Avoid focusing the iframe: embedded video / controls can steal focus and make
          // subsequent parent-driven navigation feel “stuck” across windows.
          const base = {
            bubbles: true,
            cancelable: true,
            view: window,
            clientX: x,
            clientY: y,
            button: 0,
          };
          try {
            el.dispatchEvent(
              new PointerEvent('pointerdown', {
                ...base,
                pointerId: 1,
                pointerType: 'mouse',
                isPrimary: true,
                pressure: 0.5,
              })
            );
            el.dispatchEvent(
              new PointerEvent('pointerup', {
                ...base,
                pointerId: 1,
                pointerType: 'mouse',
                isPrimary: true,
                buttons: 0,
                pressure: 0,
              })
            );
          } catch {
            /* PointerEvent unsupported in very old browsers */
          }
          el.dispatchEvent(new MouseEvent('click', { ...base, detail: 1 }));
          return true;
        };
        const a = aRef.current;
        const b = bRef.current;
        if (tryNudge(a)) {
          queueMicrotask(blurBothPanes);
          return;
        }
        if (tryNudge(b)) {
          queueMicrotask(blurBothPanes);
        }
      },
    }),
    []
  );

  const fireReady = () => {
    try {
      onReadyRef.current?.();
    } catch {
      /* ignore */
    }
  };

  useLayoutEffect(() => {
    if (!src) return;
    const a = aRef.current;
    const b = bRef.current;
    if (!a || !b) return;

    if (prevSrcRef.current === null) {
      prevSrcRef.current = src;
      a.src = src;
      a.style.visibility = 'visible';
      a.style.zIndex = '2';
      b.src = 'about:blank';
      b.style.visibility = 'hidden';
      b.style.zIndex = '1';
      activePaneRef.current = 0;
      const gen = ++loadGenRef.current;
      let firstReadyFired = false;
      const fireReadyOnce = () => {
        if (firstReadyFired) return;
        firstReadyFired = true;
        fireReady();
      };
      const onFirstLoad = () => {
        a.removeEventListener('load', onFirstLoad);
        if (gen !== loadGenRef.current) return;
        fireReadyOnce();
      };
      a.addEventListener('load', onFirstLoad);
      // Cached iframes often fire load before we attach; treat as ready.
      try {
        if (a.contentDocument?.readyState === 'complete') {
          a.removeEventListener('load', onFirstLoad);
          if (gen === loadGenRef.current) fireReadyOnce();
        }
      } catch {
        /* cross-origin */
      }
      const firstFallback = window.setTimeout(() => {
        if (gen !== loadGenRef.current) return;
        a.removeEventListener('load', onFirstLoad);
        fireReadyOnce();
      }, SLIDE_LOAD_FALLBACK_MS);
      return () => {
        clearTimeout(firstFallback);
        a.removeEventListener('load', onFirstLoad);
      };
    }

    if (prevSrcRef.current === src) return;

    const next = 1 - activePaneRef.current;
    const showEl = next === 0 ? a : b;
    const hideEl = next === 0 ? b : a;
    const gen = ++loadGenRef.current;

    let fallbackTimer = null;
    const clearFallback = () => {
      if (fallbackTimer != null) {
        clearTimeout(fallbackTimer);
        fallbackTimer = null;
      }
    };

    const applySwap = () => {
      // Bring the loaded pane on top first, then hide the old pane — avoids a one-frame gap
      // where both are hidden and the black stack background shows through.
      showEl.style.zIndex = '2';
      showEl.style.visibility = 'visible';
      hideEl.style.zIndex = '1';
      hideEl.style.visibility = 'hidden';
      activePaneRef.current = next;
      setVisiblePane(next);
      prevSrcRef.current = src;
      fireReady();
    };

    const onLoad = () => {
      showEl.removeEventListener('load', onLoad);
      clearFallback();
      if (gen !== loadGenRef.current) return;
      applySwap();
    };

    showEl.addEventListener('load', onLoad);
    showEl.src = src;

    try {
      if (showEl.contentDocument?.readyState === 'complete') {
        showEl.removeEventListener('load', onLoad);
        if (gen === loadGenRef.current) {
          clearFallback();
          applySwap();
        }
      }
    } catch {
      /* cross-origin */
    }

    fallbackTimer = window.setTimeout(() => {
      fallbackTimer = null;
      if (gen !== loadGenRef.current) return;
      if (prevSrcRef.current === src) return;
      try {
        showEl.removeEventListener('load', onLoad);
      } catch {
        /* ignore */
      }
      applySwap();
    }, SLIDE_LOAD_FALLBACK_MS);

    return () => {
      clearFallback();
      showEl.removeEventListener('load', onLoad);
    };
  }, [src]);

  return (
    <div className="slides-embed-stack">
      <iframe
        ref={aRef}
        title={title || 'Presentation'}
        className="slides-embed-pane"
        width="100%"
        height="100%"
        frameBorder="0"
        muted={visiblePane !== 0 ? true : embedMuted}
        allow="autoplay; fullscreen; encrypted-media; picture-in-picture; clipboard-write"
        allowFullScreen={allowFullScreen}
      />
      <iframe
        ref={bRef}
        title={`${title || 'Presentation'} (standby)`}
        className="slides-embed-pane"
        aria-hidden="true"
        width="100%"
        height="100%"
        frameBorder="0"
        muted={visiblePane !== 1 ? true : embedMuted}
        allow="autoplay; fullscreen; encrypted-media; picture-in-picture; clipboard-write"
        allowFullScreen={allowFullScreen}
      />
    </div>
  );
}

export default forwardRef(DualGoogleSlidesEmbed);
